import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import * as moment from 'moment';
import { debounceTime } from 'rxjs';

import { PuSubscribable } from '@app/utils/pu-subscribe';
import { CustomFieldsService } from '@main-application/management/pages/property-model/services/custom-fields.service';
import { TimezoneService } from '@main-application/management/pages/system-configuration/components/date-time-configuration/timezone.service';
import { AreaPageElementQuestionModel } from '@main-application/shared/template-editor-dialog/models/template-editor-model';
import { TemplatePlaceholder } from '@main-application/shared/template-editor-dialog/services/template-editor-dialog.service';
import { EColorPalette } from '@shared/enums/color-palette.enum';
import { EIcon } from '@shared/enums/icon.enum';
import { IRadioButtonOption } from '@shared/interfaces/radio-button-option.interface';
import { CalendarHeaderComponent } from '@ui-components/components/calendar-header/calendar-header.component';
import { IconComponent } from '@ui-components/components/icon/icon.component';
import { InputModule } from '@ui-components/controls/input/input.module';
import { InputAreaModule } from '@ui-components/controls/input-area/input-area.module';
import { RadioButtonModule } from '@ui-components/controls/radio-button/radio-button.module';
import { RadioButtonDropdownModule } from '@ui-components/controls/radio-button-dropdown/radio-button-dropdown.module';
import { DialogResult } from '@ui-components/modals/config/dialog-result.enum';

@Component({
  selector: 'app-template-editor-value-modal',
  templateUrl: './template-editor-value-modal.component.html',
  styleUrls: ['./template-editor-value-modal.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    IconComponent,
    InputModule,
    InputAreaModule,
    MatDatepickerModule,
    RadioButtonDropdownModule,
    RadioButtonModule,
  ],
})
export class TemplateEditorValueModalComponent extends PuSubscribable implements OnInit {
  public readonly calendarHeader = CalendarHeaderComponent;
  EIcon = EIcon;
  EColorPalette = EColorPalette;

  form: FormGroup;

  private allPlaceholders: TemplatePlaceholder[] = [];
  placeholders: TemplatePlaceholder[] | null = null;
  dropDownOptions: IRadioButtonOption<string>[][] = [];
  dateTimeOptions: { value: Date; label: string }[] = [];
  tempDate: moment.Moment | null = null;
  tempTime: moment.Moment | null = null;

  get title(): FormControl {
    return this.form.get('title') as FormControl;
  }
  get defaultValue(): FormControl {
    return this.form.get('defaultValue') as FormControl;
  }

  constructor(
    public dialogRef: MatDialogRef<
      TemplateEditorValueModalComponent,
      {
        result: DialogResult;
        data: { question: AreaPageElementQuestionModel; placeholders: TemplatePlaceholder[]; useDynamicValue: boolean };
      }
    >,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      question: AreaPageElementQuestionModel;
      placeholders: TemplatePlaceholder[];
      useDynamicValue: boolean;
    },
    private fb: FormBuilder,
    private timezoneService: TimezoneService,
    private customFieldsService: CustomFieldsService
  ) {
    super();
  }

  ngOnInit(): void {
    if (this.data.useDynamicValue && this.data.placeholders?.length > 0) {
      this.placeholders = this.data.placeholders;
      this.allPlaceholders = [...this.data.placeholders];

      const current = this.data?.question?.defaultValue;
      const exists = this.placeholders?.some(p => p.value === current);

      if (!exists) {
        this.data.question.defaultValue = null;
      }
    } else {
      this.placeholders = this.data.placeholders;
      const current = this.data?.question?.defaultValue;
      const exists = this.placeholders?.some(p => p.value === current);

      if (exists) {
        this.data.question.defaultValue = null;
      }
    }

    let initialDefaultValue = this.data?.question?.defaultValue ?? null;
    if (
      this.data?.question?.type === 'money' &&
      initialDefaultValue != null &&
      typeof initialDefaultValue === 'number'
    ) {
      initialDefaultValue = this.customFieldsService.formatAmount(initialDefaultValue);
    }

    this.form = this.fb.group({
      title: this.data?.question?.title ?? null,
      defaultValue: initialDefaultValue,
      dateTimeValueTime: null,
      placeholders: this.data.placeholders,
      search: [''],
    });

    if (this.data?.question?.type === 'dropdown' && Array.isArray(this.data?.question?.options)) {
      const groupedMap = new Map<string | null, IRadioButtonOption<string>[]>();

      this.data.question?.options.forEach(opt => {
        if (!opt || typeof opt !== 'object') return;

        const value = opt.value;
        const label = opt.label;
        const color = typeof opt.color === 'string' ? opt.color : undefined;
        const group = typeof opt.group === 'string' ? opt.group : null;

        if (!value || typeof label !== 'string') return;

        const item: IRadioButtonOption<string> = { value, label, ...(color ? { color } : {}) };

        if (!groupedMap.has(group)) groupedMap.set(group, []);
        groupedMap.get(group)!.push(item);
      });

      this.dropDownOptions = Array.from(groupedMap.entries()).map(([group, items]) => {
        if (group && items?.length > 0) items.forEach(item => (item.groupLabel = group));
        return items;
      });

      if (this.data?.question?.defaultValue) {
        this.form.get('defaultValue')?.setValue(this.data.question?.defaultValue, { emitEvent: false });
      }
    }

    if (this.data?.question?.type === 'date') {
      const raw = this.data?.question?.defaultValue;
      this.tempDate = raw ? moment(raw as string | number | Date) : null;
    }

    if (this.data?.question?.type === 'dateTime') {
      const baseDate = this.data?.question?.defaultValue
        ? moment(this.data.question?.defaultValue as string | number | Date)
        : moment(this.timezoneService.getCurrentDateOnly());

      this.tempDate = baseDate;
      this.dateTimeOptions = this.customFieldsService.getDateTimes(baseDate.toDate());

      if (this.data?.question?.defaultValue) {
        this.tempTime = baseDate.clone();
        const match = this.dateTimeOptions.find(
          opt =>
            moment(opt.value).hour() === this.tempTime.hour() && moment(opt.value).minute() === this.tempTime.minute()
        );
        if (match) {
          this.form.get('dateTimeValueTime')?.setValue(match.value, { emitEvent: false });
        }
      }

      this.form
        .get('dateTimeValueTime')
        ?.valueChanges.subscribe((value: Date) => {
          this.tempTime = value ? moment(value) : null;
        })
        .untilDestroyed(this);
    }

    if (this.data.useDynamicValue && this.data.placeholders?.length > 0) {
      this.form
        .get('search')!
        .valueChanges.pipe(debounceTime(300))
        .subscribe(() => this.onSearch())
        .untilDestroyed(this);
    }

    this.dialogRef
      .backdropClick()
      .subscribe(() => this.save())
      .untilDestroyed(this);
  }

  save(): void {
    let newDefault: string | number | null = this.defaultValue.value;

    if (this.data.useDynamicValue && this.data.placeholders?.length > 0) {
      const selected = this.placeholders?.find(p => p.value === newDefault);
      newDefault = selected ? selected.value : null;
    } else {
      if (this.data?.question?.type === 'date') {
        if (!this.tempDate || !this.tempDate.isValid()) {
          newDefault = null;
        } else {
          newDefault = this.tempDate.format('YYYY-MM-DD');
        }
      }

      if (this.data?.question?.type === 'dateTime') {
        if (!this.tempDate || !this.tempTime || !this.tempDate.isValid() || !this.tempTime.isValid()) {
          newDefault = null;
        } else {
          const combined = this.tempDate.clone().hour(this.tempTime.hour()).minute(this.tempTime.minute());
          newDefault = combined.format('YYYY-MM-DD HH:mm');
        }
      }

      if (this.data?.question?.type === 'money') {
        const rawValue = this.defaultValue.value;

        newDefault =
          rawValue === null || rawValue === undefined || (typeof rawValue === 'string' && rawValue.trim() === '')
            ? null
            : this.customFieldsService.parseAmount(rawValue.toString());
      }
    }

    const result: {
      question: AreaPageElementQuestionModel;
      placeholders: TemplatePlaceholder[];
      useDynamicValue: boolean;
    } = {
      ...this.data,
      question: {
        ...this.data.question,
        title: this.title.value,
        defaultValue: newDefault,
      },
    };

    this.dialogRef.close({ result: DialogResult.Success, data: result });
  }

  cancel(): void {
    this.dialogRef.close();
  }

  onDateSelected(date: Date): void {
    this.tempDate = moment(date);
    this.dateTimeOptions = this.customFieldsService.getDateTimes(date);

    if (this.tempTime) {
      const preserved = this.tempDate.clone().hour(this.tempTime.hour()).minute(this.tempTime.minute());

      this.tempTime = preserved;

      const match = this.dateTimeOptions.find(
        opt =>
          moment(opt.value).hour() === this.tempTime.hour() && moment(opt.value).minute() === this.tempTime.minute()
      );

      if (match) {
        this.form.get('dateTimeValueTime')?.setValue(match.value, { emitEvent: false });
      }
    }
  }

  onTimeSelected(value: Date): void {
    this.tempTime = value ? moment(value) : null;
    this.form.get('dateTimeValueTime')?.setValue(value);
  }

  onSearch(): void {
    const query: string = (this.form.get('search')?.value || '').toLowerCase().trim();

    if (!query) {
      this.placeholders = [...this.allPlaceholders];
      return;
    }

    this.placeholders = this.allPlaceholders.filter(
      p => p.label.toLowerCase().includes(query) || String(p.value).toLowerCase().includes(query)
    );
  }

  onClearDate(): void {
    this.form.get('defaultValue')?.setValue(null, { emitEvent: false });
    this.tempDate = null;
  }
}
